// src/logger.ts
import log, { LogLevelNames, LogLevelNumbers } from "loglevel";

// Получаем доступ к URL-параметрам с помощью стандартного браузерного API.
const queryParams = new URLSearchParams(window.location.search);

// Проверяем, есть ли в URL параметр `remotejs`.
// Это наш флаг для активации интеграции.
if (queryParams.has("remotejs")) {
	log.debug("Remotejs detected, applying custom loglevel method factory");

	/**
	 * Оригинальная фабрика методов loglevel.
	 * Мы сохраняем ее, чтобы можно было использовать стандартное форматирование loglevel.
	 */
	const originalFactory = log.methodFactory;

	/**
	 * Кастомная фабрика методов для loglevel, активируется только для remotejs.
	 * Эта функция заменяет стандартный способ вывода сообщений loglevel.
	 * Она вызывает и стандартный метод loglevel (для локальной консоли),
	 * и глобальный метод console (который перехватывается remotejs).
	 *
	 * @param {log.LogLevelNames} methodName - Имя уровня логирования ("info", "warn" и т.д.).
	 * @param {log.LogLevelNumbers} level - Числовое представление уровня.
	 * @param {string | symbol} loggerName - Имя логгера.
	 * @returns {log.LoggingMethod} Функция, которая будет выполнять логирование.
	 */
	log.methodFactory = (
		methodName: LogLevelNames,
		level: LogLevelNumbers,
		loggerName: string | symbol
	): log.LoggingMethod => {
		const rawMethod = originalFactory(methodName, level, loggerName);

		return (...args: unknown[]) => {
			// 1. Вызываем оригинальный метод для вывода в локальную консоль с форматированием.
			// rawMethod(...args);

			// 2. Явно дублируем вызов на глобальный объект `console` для `remotejs`.
			// Проверяем, что такой метод в `console` вообще существует.
			// Уровни loglevel ('trace', 'debug', 'info', 'warn', 'error') напрямую соответствуют
			// именам методов в `console`.
			// eslint-disable-next-line no-console
			if (methodName in console && typeof console[methodName as keyof Console] === "function") {
				// TypeScript не может проверить тип при динамическом вызове, поэтому используем `as any`.
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(console as any)[methodName](...args);
			}
		};
	};
} else {
	log.debug("Using standard loglevel method factory");
}

// Устанавливаем уровень логирования в зависимости от переменной окружения.
// Эта настройка применяется в любом случае, и для remotejs, и для обычного режима.
log.setDefaultLevel(
	import.meta.env.VITE_APP_ENV_OVERRIDE_NAME === "prod"
		? "warn"
		: import.meta.env.VITE_APP_ENV_OVERRIDE_NAME === "dev"
			? "trace"
			: "debug"
);

// Включаем все уровни, чтобы setDefaultLevel работал корректно.
// loglevel сначала отключает все логи, а setDefaultLevel лишь устанавливает "фильтр".
// enableAll() "включает" их все до этого фильтра.
log.enableAll();

// Экспортируем настроенный экземпляр логгера для использования во всем приложении.
export default log;
